import { Component, OnInit, forwardRef, Input, OnChanges, SimpleChanges } from '@angular/core';
import { ControlValueAccessor, FormGroup, FormBuilder, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Address, AddressConfig, AddressErrors, AustralianStates } from './address.model';
import { LookupService } from 'src/app/shared/services/lookup.service';
import { Country, LookupData } from 'src/app/shared/models/lookup-data.model';
import { ApiResponse } from 'src/app/shared/models/api-response.model';
import { AddressService } from '../../../shared/services/address.service';
@Component({
  selector: 'online-app-address',
  templateUrl: './address.component.html',
  styleUrls: ['./address.component.scss'],
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => AddressComponent),
    multi: true
  }]
})
export class AddressComponent implements OnInit, OnChanges, ControlValueAccessor {

  constructor(
    private addressService: AddressService
  ) {
    this.selectedAustralianState = this.getDefaultAustralianState();
    // this.selectedCountry = this.getDefaultCountry();
   }

  disabled = false;
  australianStates: any[];
  countries: Country[];
  overseasAddressCountryList: Country[];


  @Input() config: AddressConfig;
  @Input() errors: AddressErrors;
  @Input() data: Address;

  title = '';
  selectedCountry: LookupData;
  selectedAustralianState: LookupData;

  private _value: Address = {
                              line1: undefined,
                              line2: undefined,
                              suburb: undefined,
                              state: undefined,
                              postcode: undefined,
                              country: undefined,
                              isCurrentAddress: undefined
                            };

  private _onChange = (_: any) => {};
  private _onTouched = (_: any) => {};

  ngOnInit() {

    if (this.config.isAustralianAddress) {
      this.australianStates = [this.getDefaultAustralianState(), ...AustralianStates ] ;
      this.title = 'Australian Address';
    } else {
      this.title = 'Overseas Address';

      this.filterSetCountryList();
    }
    if (this.config.isAustralianAddress) {
      this.addressService.loadAddressWidget('aus-address-line1', this, this.setAddressPart);
    }
  }
  private setAddressPart(container, line1Ctl,  line1: string, suburb: string, state: string, postcode: string) {
    setTimeout(() => {
      container._value.line1 = line1;
      line1Ctl.blur();
    }, 10);
    container._value.line2 = '';
    container._value.suburb = suburb;
    container._value.state = state;
    container._value.postcode = postcode;
    if (container.errors) {
      container.errors.line1 ='';
      container.errors.line2 ='';
      container.errors.suburb ='';
      container.errors.state ='';
      container.errors.postcode ='';
    }
  } 
  ngOnChanges(changes: SimpleChanges): void {

    this.countries = this.config.countries;
    
    if (changes.data && changes.data.currentValue) {
      this.value = changes.data.currentValue;
      this.filterSetCountryList();

      if (this.config.isAustralianAddress) {
        const { state } = changes.data.currentValue;
        this.stateUpdate(state);

      } else {
        const { country } = changes.data.currentValue;
        this.countryUpdate(country);
      }

    }

  }

  getSuburbText(): string {
    return this.config.isAustralianAddress ? 'Suburb' : 'Suburb/Town/City';
  }

  getStateText(): string {
    return this.config.isAustralianAddress ? 'State' : 'State/Province/Region';
  }

  getPostcodeText(): string {
    return this.config.isAustralianAddress ? 'Postcode' : 'Postcode/Zipcode';
  }

  getDefaultCountry(): Country {
    return {
      Id: '',
      Code: '',
      Description: 'Please select',
      Status: '',
      IsAgentMandatory: false,
      CanStudyAbroad: false,
      PhoneCode: 0,
      IsDeleted: false
    };
  }

  getDefaultAustralianState(): LookupData  {
    return {
      Code: '',
      Description: 'Please select',
    };
  }



  set value(value: Address) {
    this._value = value;
    this._onChange(this._value);
  }

  get value() {
    return this._value;
  }


  writeValue(value: Address): void {
    if (value && value !== null) {
      this._value = value;
    }
  }

  registerOnChange(fn: (_: any) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: (_: any) => void): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  line1Update(event: any) {
    this._updateValue(event.target.value, 'line1');
    this.errors.line1 = '';
  }

  line2Update(event: any) {
    this._updateValue(event.target.value, 'line2');
    this.errors.line2 = '';
  }

  suburbUpdate(event: any) {
    this._updateValue(event.target.value, 'suburb');
    this.errors.suburb = '';
  }

  stateInputUpdate(event: any) {
    this._updateValue(event.target.value, 'state');
    this.errors.state = '';
  }

  stateUpdate(code: any) {
    this._updateValue(code, 'state');
    this.errors.state = '';
  }

  postcodeUpdate(event: any) {
    this._updateValue(event.target.value, 'postcode');
    this.errors.postcode = '';
  }

  countryUpdate(code: any) {
    this._updateValue(code, 'country');
    this.errors.country = '';
  }

  isCurrentAddressClicked(event) {
    if (this.config.isSubmittedApplication) {
      return;
    }
    this.value.isCurrentAddress = event.checked;
    const isCurrentAddressNewValue = this.value.isCurrentAddress;
    this._updateValue(isCurrentAddressNewValue, 'isCurrentAddress');
    this.errors.isCurrentAddress = '';
  }

  private _updateValue(value: any, field: string) {
    const newValue = this._value;
    newValue[field] = value;
    this.value = newValue;
  }

  private _isValid(): boolean {

    const address = this._value;

    return address &&
          !( address === null ||
             !address.line1 ||
             !address.line2 ||
             !address.suburb ||
             !address.state ||
             !address.postcode ||
             !address.country);
  }

  filterSetCountryList() {

    if (this.config && this.config.countries) {
      let allCountries = this.config.countries;  

      this.overseasAddressCountryList = allCountries.filter(c => c.Code !== "AU");
    }
  }

  /* For accessibility, keyboard only design, use tab to show borders but showing no border by clicking on mouse */
  keyboardFocus(event): void {
    event.style.outline = "1px solid blue";
  }

  keyboardBlur(event): void {
    event.style.outline = "";
  }
}
